(() => {
  const MODE_STORAGE_PREFIX = 'customer-favorites-mode-';
  const GLOBAL_MODE_STORAGE_KEY = 'customer-favorites-mode';

  class CustomerFavoritesController {
    constructor(root) {
      this.root = root;
      this.form = document.forms[root.getAttribute('form')];
      this.productId = root.getAttribute('data-product-id');
      this.config = this.parseConfig();
      this.mode = this.getInitialMode();
      this.isApplyingVariantFallback = false;
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onFavoritesVariantChange = this.handleFavoritesVariantChange.bind(this);
    }

    parseConfig() {
      const script = this.root.querySelector('[data-customer-favorites-config]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Failed to parse Customer Favorites config', error);
        return null;
      }
    }

    getInitialMode() {
      const storedMode =
        window.localStorage.getItem(GLOBAL_MODE_STORAGE_KEY) ||
        window.localStorage.getItem(`${MODE_STORAGE_PREFIX}${this.productId}`);
      const initialMode = this.root.dataset.customerFavoritesInitialMode || 'all-options';
      return storedMode || initialMode;
    }

    init() {
      if (!this.config) return;

      this.bindTabs();

      this.bindCustomerFavoritesVariantProxy();

      this.bindVariantChangeSync();

      document.addEventListener('product-info:loaded', (event) => {
        if (event.target === this.root) {
          window.requestAnimationFrame(() => this.applyMode());
        }
      });

      this.applyMode();
    }

    setMode(mode) {
      this.mode = mode === 'customer-favorites' ? 'customer-favorites' : 'all-options';
      window.localStorage.setItem(GLOBAL_MODE_STORAGE_KEY, this.mode);
      window.localStorage.setItem(`${MODE_STORAGE_PREFIX}${this.productId}`, this.mode);
      this.applyMode();
    }

    applyMode() {
      this.syncTabs();
      this.applyBlockVisibility();
      this.applyVariantPickerMode();
      this.applyFieldRules();
      this.applyVariantRules();
      this.persistCurrentLineItemState();
    }

    syncTabs() {
      this.root.querySelectorAll('[data-customer-favorites-tab]').forEach((tab) => {
        const isActive = tab.dataset.customerFavoritesTab === this.mode;
        tab.setAttribute('aria-pressed', String(isActive));
        tab.toggleAttribute('aria-current', isActive);
      });
    }

    bindTabs() {
      const tabs = Array.from(this.root.querySelectorAll('[data-customer-favorites-tab]'));

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.setMode(tab.dataset.customerFavoritesTab));
        tab.addEventListener('keydown', (event) => {
          const handledKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
          if (!handledKeys.includes(event.key)) return;

          event.preventDefault();

          let nextIndex = index;
          if (event.key === 'ArrowLeft') {
            nextIndex = index === 0 ? tabs.length - 1 : index - 1;
          } else if (event.key === 'ArrowRight') {
            nextIndex = index === tabs.length - 1 ? 0 : index + 1;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = tabs.length - 1;
          }

          const nextTab = tabs[nextIndex];
          nextTab.focus();
          this.setMode(nextTab.dataset.customerFavoritesTab);
        });
      });
    }

    applyBlockVisibility() {
      const isFavoritesMode = this.mode === 'customer-favorites';

      this.root.querySelectorAll('[data-customer-favorites-block]').forEach((block) => {
        const showInFavorites = block.dataset.showInCustomerFavorites === 'true';
        const isHidden = isFavoritesMode && !showInFavorites;
        block.hidden = isHidden;
        this.toggleBlockInputs(block, !isHidden);
      });
    }

    applyFieldRules() {
      const isFavoritesMode = this.mode === 'customer-favorites';
      const wrappers = this.root.querySelectorAll('[data-customer-favorites-field-key]');

      wrappers.forEach((wrapper) => {
        const fieldKey = wrapper.dataset.customerFavoritesFieldKey;
        const fieldConfig = this.config.fields?.[fieldKey] || {};
        const parentBlock = wrapper.closest('[data-customer-favorites-block]');
        const allowedValues = this.normalizeValues(fieldConfig.allowedValues);
        const hasConfiguredValues = allowedValues.length > 0;

        this.clearGeneratedPropertyInput(wrapper);

        if (parentBlock?.hidden) {
          if (isFavoritesMode) {
            this.appendGeneratedPropertyInput(wrapper, fieldConfig.defaultValue);
          }
          return;
        }

        if (!isFavoritesMode) {
          this.resetField(wrapper);
          return;
        }

        if (!hasConfiguredValues) {
          wrapper.hidden = true;
          this.disableInputs(wrapper);
          this.appendGeneratedPropertyInput(wrapper, fieldConfig.defaultValue);
          return;
        }

        this.showField(wrapper);
        this.filterFieldValues(wrapper, allowedValues);
      });
    }

    applyVariantRules() {
      const variantPicker = this.getActiveVariantPicker();
      if (!variantPicker) return;

      const isFavoritesMode = this.mode === 'customer-favorites';
      const optionWrappers = variantPicker.querySelectorAll('[data-option-position]');

      optionWrappers.forEach((wrapper) => {
        const position = wrapper.dataset.optionPosition;
        const allowedValues = isFavoritesMode
          ? this.normalizeValues(this.config.nativeOptions?.[position])
          : [];

        this.filterVariantOptionValues(wrapper, allowedValues);
      });

      if (isFavoritesMode) {
        this.ensureValidVariantSelection(variantPicker);
      }
    }

    applyVariantPickerMode() {
      const containers = Array.from(this.root.querySelectorAll('[data-customer-favorites-variant-picker-container]'));
      if (!containers.length) return;

      const activeMode = this.mode === 'customer-favorites' ? 'customer-favorites' : 'all-options';
      const nextContainer = containers.find((container) => container.dataset.pickerMode === activeMode) || containers[0];
      const currentContainer = containers.find((container) => !container.hidden) || nextContainer;

      if (currentContainer && nextContainer && currentContainer !== nextContainer) {
        this.copyVariantPickerSelections(currentContainer, nextContainer);
      }

      containers.forEach((container) => {
        const isActive = container === nextContainer;
        container.hidden = !isActive;
        container.setAttribute('aria-hidden', String(!isActive));
        this.toggleVariantPickerContainer(container, activeMode, isActive);
      });
    }

    toggleVariantPickerContainer(container, activeMode, isActive) {
      const isCustomerFavoritesContainer = container.dataset.pickerMode === 'customer-favorites';

      container.querySelectorAll('input, select, textarea, button').forEach((input) => {
        if (!input.dataset.cfBlockOriginalDisabled) {
          input.dataset.cfBlockOriginalDisabled = input.disabled ? 'true' : 'false';
        }

        let shouldDisable = false;

        if (isCustomerFavoritesContainer) {
          if (!isActive) {
            shouldDisable = true;
          } else {
            const name = input.getAttribute('name') || '';
            shouldDisable = name.startsWith('properties[');
          }
        }

        if (shouldDisable) {
          input.disabled = true;
        } else {
          input.disabled = input.dataset.cfBlockOriginalDisabled === 'true';
        }
      });
    }

    getActiveVariantPicker() {
      const activeContainer = Array.from(this.root.querySelectorAll('[data-customer-favorites-variant-picker-container]')).find((container) => !container.hidden);
      return activeContainer?.querySelector('[data-customer-favorites-variant-picker]') || this.root.querySelector('[data-customer-favorites-variant-picker]');
    }

    getVariantSelections(container) {
      const picker = container?.querySelector('[data-customer-favorites-variant-picker]');
      if (!picker) return {};

      const selections = {};
      picker.querySelectorAll('[data-option-position]').forEach((wrapper) => {
        const position = wrapper.dataset.optionPosition;
        const select = wrapper.querySelector('select');
        if (select) {
          selections[position] = select.value;
          return;
        }

        const checkedInput = wrapper.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          selections[position] = checkedInput.value;
        }
      });
      return selections;
    }

    copyVariantPickerSelections(sourceContainer, targetContainer) {
      const selections = this.getVariantSelections(sourceContainer);
      const picker = targetContainer?.querySelector('[data-customer-favorites-variant-picker]');
      if (!picker) return;

      picker.querySelectorAll('[data-option-position]').forEach((wrapper) => {
        const position = wrapper.dataset.optionPosition;
        const value = selections[position];
        if (!value) return;

        this.applyVariantPickerValue(wrapper, value);
      });
    }

    syncVariantPickerSelections(variant) {
      if (!variant?.options?.length) return;

      this.root.querySelectorAll('[data-customer-favorites-variant-picker-container]').forEach((container) => {
        const picker = container.querySelector('[data-customer-favorites-variant-picker]');
        if (!picker) return;

        picker.querySelectorAll('[data-option-position]').forEach((wrapper, index) => {
          const optionValue = variant.options[index];
          if (!optionValue) return;

          this.applyVariantPickerValue(wrapper, optionValue);
        });
      });
    }

    applyVariantPickerValue(wrapper, value) {
      const select = wrapper.querySelector('select');
      if (select) {
        select.value = value;
        Array.from(select.options).forEach((option) => option.toggleAttribute('selected', option.value === value));
        this.updateVariantPickerLabel(wrapper, value);
        return;
      }

      const radio = Array.from(wrapper.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
      if (!radio) return;

      radio.checked = true;
      this.updateVariantPickerLabel(wrapper, value);
    }

    updateVariantPickerLabel(wrapper, value) {
      const labelValue = wrapper.querySelector('.form__label .font-medium');
      if (labelValue) {
        labelValue.textContent = value;
      }
    }

    bindCustomerFavoritesVariantProxy() {
      if (this.root.dataset.cfProxyBound === 'true') return;

      this.root.dataset.cfProxyBound = 'true';
      this.root.addEventListener('change', this.onFavoritesVariantChange, true);
    }

    bindVariantChangeSync() {
      if (this.variantChangeSyncBound) return;

      this.variantChangeSyncBound = true;
      (this.form || this.root).addEventListener('variant:change', this.onVariantChange);
    }

    handleVariantChange(event) {
      window.requestAnimationFrame(() => {
        this.syncVariantPickerSelections(event.detail?.variant);
        this.applyMode();
      });
    }

    handleFavoritesVariantChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      const favoritesContainer = target.closest('[data-picker-mode="customer-favorites"]');
      const favoritesPicker = target.closest('[data-customer-favorites-variant-picker]');
      if (!favoritesContainer || !favoritesPicker || !favoritesContainer.contains(favoritesPicker)) return;

      const wrapper = target.closest('[data-option-position]');
      if (!wrapper) return;

      const canonicalPicker = this.root.querySelector('[data-picker-mode="all-options"] [data-customer-favorites-variant-picker]');
      if (!canonicalPicker) return;

      event.stopImmediatePropagation();

      const position = wrapper.dataset.optionPosition;
      const canonicalWrapper = canonicalPicker.querySelector(`[data-option-position="${CSS.escape(position)}"]`);
      if (!canonicalWrapper) return;

      const value = target.value;
      const canonicalSelect = canonicalWrapper.querySelector('select');
      if (canonicalSelect && !Array.from(canonicalSelect.options).some((option) => option.value === value)) return;

      this.applyVariantPickerValue(wrapper, value);
      this.applyVariantPickerValue(canonicalWrapper, value);

      const canonicalTarget = canonicalSelect || Array.from(canonicalWrapper.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
      if (!canonicalTarget) return;

      canonicalPicker.syncOptionPropertyInput?.(canonicalTarget);

      this.persistCurrentLineItemState();

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target: target instanceof HTMLSelectElement ? target.selectedOptions[0] || target : target,
          selectedOptionValues: canonicalPicker.selectedOptionValues
        }
      });
    }

    filterVariantOptionValues(wrapper, allowedValues) {
      const select = wrapper.querySelector('select');
      if (select) {
        Array.from(select.options).forEach((option) => {
          if (!option.dataset.cfOriginalDisabled) {
            option.dataset.cfOriginalDisabled = option.disabled ? 'true' : 'false';
          }

          const isAllowed = allowedValues.length === 0 || allowedValues.includes(this.normalizeValue(option.value));
          option.hidden = !isAllowed;
          option.disabled = !isAllowed || option.dataset.cfOriginalDisabled === 'true';
        });
        return;
      }

      wrapper.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!input.dataset.cfOriginalDisabled) {
          input.dataset.cfOriginalDisabled = input.disabled ? 'true' : 'false';
        }

        const value = this.normalizeValue(input.dataset.optionValue || input.value);
        const isAllowed = allowedValues.length === 0 || allowedValues.includes(value);
        const container = input.closest('li') || input.closest('label') || input;

        container.classList.toggle('customer-favorites-option-hidden', !isAllowed);
        input.disabled = !isAllowed || input.dataset.cfOriginalDisabled === 'true';
      });
    }

    ensureValidVariantSelection(variantPicker) {
      if (this.isApplyingVariantFallback) return;

      this.isApplyingVariantFallback = true;

      try {
        Array.from(variantPicker.querySelectorAll('[data-option-position] select')).forEach((select) => {
          const selectedOption = select.selectedOptions[0];
          if (!selectedOption || (!selectedOption.hidden && !selectedOption.disabled)) return;

          const fallbackOption = Array.from(select.options).find((option) => !option.hidden && !option.disabled);
          if (!fallbackOption) return;

          this.applyVariantPickerValue(select.closest('[data-option-position]'), fallbackOption.value);
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });

        Array.from(variantPicker.querySelectorAll('[data-option-position]')).forEach((wrapper) => {
          const checkedInput = wrapper.querySelector('input[type="radio"]:checked');
          if (!checkedInput || !checkedInput.disabled) return;

          const fallbackInput = Array.from(wrapper.querySelectorAll('input[type="radio"]')).find((input) => !input.disabled);
          if (!fallbackInput) return;

          this.applyVariantPickerValue(wrapper, fallbackInput.value);
          fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      } finally {
        this.isApplyingVariantFallback = false;
      }
    }

    filterFieldValues(wrapper, allowedValues) {
      this.restoreInputs(wrapper);
      this.showAllFieldValues(wrapper);

      if (allowedValues.length === 0) return;

      const select = wrapper.querySelector('.line-item-select');
      if (select) {
        Array.from(select.options).forEach((option) => {
          const isAllowed = allowedValues.includes(this.normalizeValue(option.value));
          option.hidden = !isAllowed;
          option.disabled = !isAllowed;
        });

        if (!select.selectedOptions[0] || select.selectedOptions[0].hidden || select.selectedOptions[0].disabled) {
          const fallbackOption = Array.from(select.options).find((option) => !option.hidden && !option.disabled);
          if (fallbackOption) {
            select.value = fallbackOption.value;
            Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
            fallbackOption.setAttribute('selected', '');
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      const radioInputs = Array.from(wrapper.querySelectorAll('.line-item-input[type="radio"]'));
      if (radioInputs.length) {
        radioInputs.forEach((input) => {
          const inputValue = this.normalizeValue(input.dataset.optionValue || input.dataset.optionColor || input.value);
          const isAllowed = allowedValues.includes(inputValue);
          const container = input.closest('li') || input.closest('.color_swatch__wrapper') || input.closest('label') || input;

          container.classList.toggle('customer-favorites-option-hidden', !isAllowed);
          input.disabled = !isAllowed;
        });

        const checkedInput = wrapper.querySelector('.line-item-input[type="radio"]:checked');
        if (!checkedInput || checkedInput.disabled) {
          const fallbackInput = radioInputs.find((input) => !input.disabled);
          if (fallbackInput) {
            fallbackInput.checked = true;
            fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      wrapper.querySelectorAll('.configuration .option').forEach((option) => {
        const optionValue = this.normalizeValue(option.dataset.optionValue || '');
        const isAllowed = allowedValues.includes(optionValue);
        const input = option.querySelector('input[type="radio"]');

        option.classList.toggle('customer-favorites-option-hidden', !isAllowed);
        if (input) {
          if (!input.dataset.cfOriginalDisabled) {
            input.dataset.cfOriginalDisabled = input.disabled ? 'true' : 'false';
          }

          input.disabled = !isAllowed || input.dataset.cfOriginalDisabled === 'true';
        }
      });

      wrapper.querySelectorAll('[data-product-variation-option]').forEach((option) => {
        const optionValue = this.normalizeValue(option.dataset.optionValue || '');
        const isAllowed = allowedValues.includes(optionValue);
        option.classList.toggle('customer-favorites-option-hidden', !isAllowed);
      });

      wrapper.querySelectorAll('.metal-color').forEach((link) => {
        const value = this.normalizeValue(link.dataset.optionColor || link.dataset.optionValue || '');
        const swatchWrapper = link.closest('.color_swatch__wrapper') || link;
        const isAllowed = allowedValues.includes(value);

        swatchWrapper.classList.toggle('customer-favorites-option-hidden', !isAllowed);
      });
    }

    appendGeneratedPropertyInput(wrapper, defaultValue) {
      if (defaultValue === null || defaultValue === undefined || defaultValue === '') return;

      const propertyName = wrapper.dataset.customerFavoritesPropertyName;
      if (!propertyName || !this.form) return;

      const helperInput = document.createElement('input');
      helperInput.type = 'hidden';
      helperInput.name = `properties[${propertyName}]`;
      helperInput.value = defaultValue;
      helperInput.dataset.customerFavoritesGenerated = 'true';
      helperInput.dataset.customerFavoritesPropertyName = propertyName;
      this.form.appendChild(helperInput);
    }

    showField(wrapper) {
      wrapper.hidden = false;
      this.restoreInputs(wrapper);
    }

    resetField(wrapper) {
      wrapper.hidden = false;
      this.restoreInputs(wrapper);
      this.showAllFieldValues(wrapper);
    }

    disableInputs(wrapper) {
      wrapper.querySelectorAll('input, select, textarea').forEach((input) => {
        if (!input.dataset.cfOriginalRequired) {
          input.dataset.cfOriginalRequired = input.required ? 'true' : 'false';
        }

        input.required = false;
        input.disabled = true;
      });
    }

    restoreInputs(wrapper) {
      wrapper.querySelectorAll('input, select, textarea').forEach((input) => {
        if (input.dataset.cfOriginalDisabled) {
          input.disabled = input.dataset.cfOriginalDisabled === 'true';
        } else {
          input.disabled = false;
        }

        if (input.dataset.cfOriginalRequired) {
          input.required = input.dataset.cfOriginalRequired === 'true';
        }
      });
    }

    showAllFieldValues(wrapper) {
      wrapper.querySelectorAll('.customer-favorites-option-hidden').forEach((element) => {
        element.classList.remove('customer-favorites-option-hidden');
      });

      wrapper.querySelectorAll('option').forEach((option) => {
        option.hidden = false;
        option.disabled = false;
      });
    }

    clearGeneratedPropertyInput(wrapper) {
      const propertyName = wrapper.dataset.customerFavoritesPropertyName;
      if (!propertyName || !this.form) return;

      this.form.querySelectorAll(`[data-customer-favorites-generated][data-customer-favorites-property-name="${CSS.escape(propertyName)}"]`).forEach((input) => {
        input.remove();
      });
    }

    toggleBlockInputs(block, enabled) {
      block.querySelectorAll('input, select, textarea, button').forEach((input) => {
        if (!input.dataset.cfBlockOriginalDisabled) {
          input.dataset.cfBlockOriginalDisabled = input.disabled ? 'true' : 'false';
        }

        if (enabled) {
          input.disabled = input.dataset.cfBlockOriginalDisabled === 'true';
        } else {
          input.disabled = true;
        }
      });
    }

    persistCurrentLineItemState() {
      const templateName = document.querySelector('[data-template]')?.getAttribute('data-template');
      if (!templateName || typeof this.root.setTemplateLineItems !== 'function') return;
      this.root.setTemplateLineItems(templateName, 'OnLoad');
    }

    normalizeValues(value) {
      if (Array.isArray(value)) {
        return value
          .flatMap((item) => this.normalizeValues(item))
          .filter(Boolean);
      }

      if (typeof value !== 'string') return [];

      return value
        .split(/[\n,]/)
        .map((item) => this.normalizeValue(item))
        .filter(Boolean);
    }

    normalizeValue(value) {
      return String(value || '').trim().toLowerCase();
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-customer-favorites-root][data-customer-favorites-enabled="true"]').forEach((root) => {
      const controller = new CustomerFavoritesController(root);
      controller.init();
    });
  });
})();
